import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
    IconAlertTriangle,
    IconArrowRight,
    IconArrowDownLeft,
    IconArrowUpRight,
    IconChevronLeft,
    IconChevronRight,
    IconCircleCheck,
    IconExternalLink,
    IconMoneybag,
    IconPercentage,
    IconRefresh,
} from "@tabler/icons-react";

import { Button, Card, StatusChip, Text } from "@fragments";
import Parser from "@helpers/parser.js";
import {
    createStripeConnectLink,
    WALLET_PROVIDERS,
    walletBalanceCurrency,
    walletHeadlineBalance,
    walletIsConnected,
    walletWithdrawalPath,
} from "@helpers/wallets";
import { ReactComponent as BrazilIcon } from "@assets/custom-icons/brazil.svg";
import { ReactComponent as GlobalIcon } from "@assets/custom-icons/global.svg";
// WebP wordmark — the CRA svg pipeline (SVGR) only takes real SVG sources.
import stripeLogo from "@assets/logo/partners/stripe-logo.webp";

import "./styles.scss";

const QUICK_WITHDRAWAL_VALUES = [100000, 500000, 1000000, 10000000];

// Built from the caller's `t` rather than declared at module level, so every
// label follows the active language. `providerName` and `Icon` are brand and
// presentation data and stay untranslated.
const getCardConfig = (t) => ({
    [WALLET_PROVIDERS.ASAAS]: {
        eyebrow: t("wallets.asaas.eyebrow"),
        title: t("wallets.asaas.title"),
        providerName: null,
        Icon: BrazilIcon,
    },
    [WALLET_PROVIDERS.STRIPE]: {
        eyebrow: t("wallets.stripe.eyebrow"),
        title: t("wallets.stripe.title"),
        providerName: "Stripe Express",
        Icon: GlobalIcon,
    },
});

function normalizeStatus(value) {
    return String(value || "").toLowerCase();
}

function resolveAsaasStatus(t, wallet) {
    const general = normalizeStatus(wallet?.statuses?.general);
    const documentation = normalizeStatus(wallet?.statuses?.documentation);

    if (walletIsConnected(wallet)) {
        return {
            chip: <StatusChip.Success text={t("wallets.chip.active")} icon={<IconCircleCheck />} />,
            receiving: t("wallets.receiving.active"),
            payouts: t("wallets.payouts.pixConfigured"),
            receivingPositive: true,
            actionText: t("wallets.actions.track"),
            actionIcon: <IconArrowRight />,
        };
    }

    if (["rejected", "refused", "denied"].includes(general)) {
        return {
            chip: <StatusChip.Error text={t("wallets.chip.review")} icon={<IconAlertTriangle />} />,
            receiving: t("wallets.receiving.reviewData"),
            payouts: t("wallets.payouts.pending"),
            receivingPositive: false,
            actionText: t("wallets.actions.review"),
            actionIcon: <IconArrowRight />,
        };
    }

    if (wallet?.id || ["awaiting_approval", "pending"].includes(documentation || general)) {
        return {
            chip: <StatusChip.Info text={t("wallets.chip.underAnalysis")} icon={<IconRefresh />} />,
            receiving: t("wallets.receiving.underAnalysis"),
            payouts: t("wallets.payouts.pending"),
            receivingPositive: false,
            actionText: t("wallets.actions.track"),
            actionIcon: <IconArrowRight />,
        };
    }

    return {
        chip: <StatusChip.Default text={t("wallets.chip.notConnected")} icon={<IconAlertTriangle />} />,
        receiving: t("wallets.receiving.notConnected"),
        payouts: t("wallets.payouts.pending"),
        receivingPositive: false,
        actionText: t("wallets.actions.setup"),
        actionIcon: <IconArrowRight />,
    };
}

function resolveStripeStatus(t, wallet) {
    const onboarding = wallet?.onboarding || {};

    if (walletIsConnected(wallet)) {
        return {
            chip: <StatusChip.Success text={t("wallets.chip.active")} icon={<IconCircleCheck />} />,
            receiving: t("wallets.receiving.active"),
            payouts: t("wallets.payouts.enabled"),
            receivingPositive: true,
            actionText: t("wallets.actions.openStripe"),
            actionIcon: <IconExternalLink />,
            actionType: "link",
        };
    }

    if (onboarding.chargesEnabled && onboarding.payoutsEnabled) {
        return {
            chip: <StatusChip.Alert text={t("wallets.chip.syncing")} icon={<IconRefresh />} />,
            receiving: t("wallets.receiving.approved"),
            payouts: t("wallets.payouts.waiting"),
            receivingPositive: true,
            actionText: t("wallets.actions.refreshStatus"),
            actionIcon: <IconRefresh />,
            actionType: "refresh",
        };
    }

    if (onboarding.detailsSubmitted) {
        return {
            chip: <StatusChip.Alert text={t("wallets.chip.pending")} icon={<IconRefresh />} />,
            receiving: t("wallets.receiving.underAnalysis"),
            payouts: t("wallets.payouts.pending"),
            receivingPositive: false,
            actionText: t("common:actions.continue"),
            actionIcon: <IconExternalLink />,
            actionType: "link",
        };
    }

    return {
        chip: <StatusChip.Default text={t("wallets.chip.notConnected")} icon={<IconAlertTriangle />} />,
        receiving: t("wallets.receiving.notConnected"),
        payouts: t("wallets.payouts.pending"),
        receivingPositive: false,
        actionText: t("wallets.actions.connectStripe"),
        actionIcon: <IconExternalLink />,
        actionType: "link",
    };
}

function resolveWalletStatus(t, provider, wallet) {
    return provider === WALLET_PROVIDERS.STRIPE
        ? resolveStripeStatus(t, wallet)
        : resolveAsaasStatus(t, wallet);
}

function walletHasWithdrawalAccount(wallet) {
    if (wallet?.provider === WALLET_PROVIDERS.STRIPE) {
        return !!(wallet?.onboarding?.externalAccountLast4 || wallet?.onboarding?.payoutsEnabled);
    }

    return !!(
        (wallet?.withdrawal?.key && wallet?.withdrawal?.keyType) ||
        wallet?.withdrawal?.receiver
    );
}

function readDigits(value) {
    return String(value || "").replace(/\D/g, "");
}

function formatFeePercent(value) {
    return `${String(value).replace(".", ",")}%`;
}

// Stripe's own fees on a direct charge are charged separately from the
// platform fee and deducted from the seller's settlement (Stripe Brazil
// Express pricing, verified against live payment breakdowns).
const getStripeSeparateFees = (t) => [
    { id: "cardProcessing", label: t("wallets.fees.cardProcessing"), value: t("wallets.fees.cardProcessingValue") },
    { id: "currencyConversion", label: t("wallets.fees.currencyConversion"), value: t("wallets.fees.currencyConversionValue") },
];

function WalletFees({ provider, wallet, loading }) {
    const { t } = useTranslation("banking");
    const stripeSeparateFees = useMemo(() => getStripeSeparateFees(t), [t]);
    const isStripe = provider === WALLET_PROVIDERS.STRIPE;
    const taxes = wallet?.taxes;

    if (!loading && !taxes && !isStripe) return null;

    const saleFee = taxes?.percentile != null
        ? `${formatFeePercent(taxes.percentile)} + ${Parser.price.format(taxes.fixed || 0, isStripe ? "USD" : "BRL")}`
        : null;

    return (
        <div className="wallet-account__fees">
            <div className="wallet-account__fees-head">
                <Text.label contrast={9} left={<IconPercentage />}>
                    {t("wallets.fees.title")}
                </Text.label>
            </div>

            <div className="wallet-account__fees-list">
                <div className="wallet-account__fee">
                    <Text.span contrast={7}>
                        {isStripe ? t("wallets.fees.platformSale") : t("wallets.fees.sale")}
                    </Text.span>
                    <Text.label loading={loading} contrast={9}>
                        {saleFee || "—"}
                    </Text.label>
                </div>

                {!isStripe && taxes?.anticipation != null && (
                    <div className="wallet-account__fee">
                        <Text.span contrast={7}>{t("wallets.fees.installments")}</Text.span>
                        <Text.label loading={loading} contrast={9}>
                            {t("wallets.fees.installmentsValue", { percent: formatFeePercent(taxes.anticipation) })}
                        </Text.label>
                    </div>
                )}

                {isStripe && stripeSeparateFees.map((fee) => (
                    <div className="wallet-account__fee" key={fee.id}>
                        <Text.span
                            contrast={7}
                            right={(
                                <img
                                    className="wallet-account__fee-source"
                                    src={stripeLogo}
                                    alt="Stripe"
                                />
                            )}
                        >
                            {fee.label}
                        </Text.span>
                        <Text.label loading={loading} contrast={9}>
                            {fee.value}
                        </Text.label>
                    </div>
                ))}
            </div>

            {isStripe && (
                <Text.caption contrast={6}>
                    {t("wallets.fees.stripeNote")}
                </Text.caption>
            )}
        </div>
    );
}

function QuickWithdrawal({
    provider,
    wallet,
    currency,
    onStripeWithdrawal,
}) {
    const { t } = useTranslation("banking");
    const navigate = useNavigate();
    const amountInputRef = useRef(null);
    const suggestionsRef = useRef(null);
    const [amount, setAmount] = useState("");
    const [suggestionsOverflow, setSuggestionsOverflow] = useState(false);
    const availableAmount = parseInt(wallet?.balance?.available, 10) || 0;
    const minimumAmount = provider === WALLET_PROVIDERS.ASAAS ? 500 : 1;
    const eligible = walletIsConnected(wallet) && walletHasWithdrawalAccount(wallet);
    const controlsEnabled = eligible && availableAmount >= minimumAmount;
    const parsedAmount = parseInt(amount, 10) || 0;
    const hasTypedAmount = amount.length > 0;
    const amountValid = parsedAmount >= minimumAmount && parsedAmount <= availableAmount;
    const canSubmit = controlsEnabled && amountValid;
    const actionText = provider === WALLET_PROVIDERS.STRIPE ? t("wallets.actions.openStripe") : t("wallets.quick.requestWithdrawal");
    const displayAmount = hasTypedAmount
        ? Parser.price.format(parsedAmount, currency)
        : `${currency === "USD" ? "US$" : "R$"} 0`;

    useEffect(() => {
        const element = suggestionsRef.current;
        if (!element) return undefined;

        function updateOverflow() {
            setSuggestionsOverflow(element.scrollWidth > element.clientWidth + 1);
        }

        updateOverflow();

        if (typeof ResizeObserver === "function") {
            const observer = new ResizeObserver(updateOverflow);
            observer.observe(element);

            return () => observer.disconnect();
        }

        if (typeof window === "undefined") return undefined;

        window.addEventListener("resize", updateOverflow);
        return () => window.removeEventListener("resize", updateOverflow);
    }, [availableAmount, controlsEnabled]);

    function handleAmountInput(event) {
        setAmount(readDigits(event.target.value));
    }

    function handleSuggestionClick(value) {
        if (!controlsEnabled) return;

        setAmount(String(value));
        amountInputRef.current?.focus();
    }

    function handleSuggestionsScroll(direction) {
        const element = suggestionsRef.current;
        if (!element) return;

        element.scrollBy({
            left: element.clientWidth * direction * 0.72,
            behavior: "smooth",
        });
    }

    function handleSubmit() {
        if (!canSubmit) return;

        if (provider === WALLET_PROVIDERS.STRIPE) {
            onStripeWithdrawal?.();
            return;
        }

        const path = walletWithdrawalPath(wallet);
        const separator = path.includes("?") ? "&" : "?";
        navigate(`${path}${separator}amount=${parsedAmount}`);
    }

    return (
        <div className={`wallet-account__quick ${controlsEnabled ? "" : "wallet-account__quick--disabled"}`}>
            <div className="wallet-account__quick-head">
                <Text.label contrast={9} left={<IconMoneybag />}>
                    {t("wallets.quick.title")}
                </Text.label>
            </div>

            <label className={`wallet-account__quick-amount ${controlsEnabled ? "" : "wallet-account__quick-amount--disabled"}`}>
                <Text.subheading contrast={6}>{t("wallets.quick.amountLabel")}</Text.subheading>
                <span className="wallet-account__quick-amount-value">
                    <Text.h1 weight={500} contrast={hasTypedAmount ? 10 : 5} maxLines={1}>
                        {displayAmount}
                    </Text.h1>
                </span>
                <input
                    ref={amountInputRef}
                    className="wallet-account__quick-native-input"
                    type="text"
                    inputMode="numeric"
                    autoComplete="off"
                    aria-label={t("wallets.quick.amountLabel")}
                    value={amount}
                    onChange={handleAmountInput}
                    disabled={!controlsEnabled}
                />
            </label>

            <div className={`wallet-account__quick-suggestion-shell ${suggestionsOverflow ? "wallet-account__quick-suggestion-shell--overflow" : ""}`}>
                {suggestionsOverflow && (
                    <Button
                        className="wallet-account__quick-scroll"
                        color="overlay"
                        size="small"
                        variant="text"
                        icon={<IconChevronLeft />}
                        disabled={!controlsEnabled}
                        tooltip={t("wallets.quick.previousValues")}
                        onClick={() => handleSuggestionsScroll(-1)}
                    />
                )}

                <div className="wallet-account__quick-suggestions" ref={suggestionsRef}>
                    <Button
                        color="overlay"
                        size="small"
                        disabled={!controlsEnabled}
                        onClick={() => handleSuggestionClick(availableAmount)}
                    >
                        {t("wallets.quick.allBalance", { value: Parser.price.format(availableAmount, currency) })}
                    </Button>

                    {QUICK_WITHDRAWAL_VALUES.map((value) => (
                        <Button
                            key={value}
                            color="overlay"
                            size="small"
                            disabled={!controlsEnabled || value > availableAmount || value < minimumAmount}
                            onClick={() => handleSuggestionClick(value)}
                        >
                            {Parser.price.format(value, currency)}
                        </Button>
                    ))}
                </div>

                {suggestionsOverflow && (
                    <Button
                        className="wallet-account__quick-scroll"
                        color="overlay"
                        size="small"
                        variant="text"
                        icon={<IconChevronRight />}
                        disabled={!controlsEnabled}
                        tooltip={t("wallets.quick.moreValues")}
                        onClick={() => handleSuggestionsScroll(1)}
                    />
                )}
            </div>

            {hasTypedAmount && (
                <Button
                    className="wallet-account__quick-action"
                    color="inverse"
                    size="medium"
                    startIcon={provider === WALLET_PROVIDERS.STRIPE ? <IconExternalLink /> : <IconMoneybag />}
                    disabled={!canSubmit}
                    onClick={handleSubmit}
                >
                    {actionText}
                </Button>
            )}
        </div>
    );
}

function WalletAccountCard({
    provider,
    wallet,
    loading,
    onProviderChange,
    onOpenAsaasOnboarding,
    onRefresh,
}) {
    const { t } = useTranslation("banking");
    const [submitting, setSubmitting] = useState(false);
    const cardConfig = useMemo(() => getCardConfig(t), [t]);
    const config = cardConfig[provider];
    const ProviderIcon = config.Icon;
    const connected = walletIsConnected(wallet);
    const status = resolveWalletStatus(t, provider, wallet);
    const isStripeCard = provider === WALLET_PROVIDERS.STRIPE;
    const currency = wallet?.currency || (isStripeCard ? "USD" : "BRL");
    const balanceCurrency = walletBalanceCurrency(wallet, currency);
    const available = parseInt(wallet?.balance?.available, 10) || 0;
    const pending = parseInt(wallet?.balance?.pending, 10) || 0;
    const balance = Parser.price.format(walletHeadlineBalance(wallet), balanceCurrency);
    const balanceLabel = isStripeCard ? t("wallets.details.stripeBalance") : t("wallets.details.asaasBalance");

    async function handleAction() {
        if (provider === WALLET_PROVIDERS.ASAAS) {
            onProviderChange(provider);
            onOpenAsaasOnboarding?.();
            return;
        }

        setSubmitting(true);
        try {
            if (status.actionType === "refresh") {
                await onRefresh?.();
                return;
            }

            const link = await createStripeConnectLink();
            await onRefresh?.();

            if (link?.url) {
                window.location.assign(link.url);
            }
        } catch (e) {
            console.log(e);
            window.setSnackbar?.(t("wallets.stripeLinkError"));
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <Card
            flat
            noBodyPadding
            className="wallet-account-card"
        >
            <div className="wallet-account">
                <div className="wallet-account__header">
                    <span className={`wallet-account__icon wallet-account__icon--${provider}`}>
                        <ProviderIcon />
                    </span>

                    <div className="wallet-account__heading">
                        <Text.subheading contrast={6}>{config.eyebrow}</Text.subheading>
                        <Text.h5 weight={500} contrast={9} maxLines={1}>
                            {config.title}
                        </Text.h5>
                    </div>

                    {loading ? (
                        <Text.caption loading contrast={6}>{t("common:fields.status")}</Text.caption>
                    ) : status.chip}
                </div>

                <div className="wallet-account__visual">
                    <div className="wallet-account__visual-top">
                        {config.providerName ? (
                            <Text.caption contrast={6}>{config.providerName}</Text.caption>
                        ) : (
                            <span aria-hidden="true" />
                        )}
                        <Text.caption contrast={6}>{balanceCurrency}</Text.caption>
                    </div>

                    <div className="wallet-account__balance">
                        <Text.caption contrast={6}>{balanceLabel}</Text.caption>
                        {loading ? (
                            <Text.h1 loading weight={500} contrast={10}>
                                {currency}
                            </Text.h1>
                        ) : (
                            <Text.h1 weight={500} contrast={10}>
                                {balance}
                            </Text.h1>
                        )}
                        {!loading && isStripeCard && connected && (
                            <Text.caption contrast={6}>
                                {t("wallets.details.available", { value: Parser.price.format(available, balanceCurrency) })}
                                {" · "}
                                {t("wallets.details.pending", { value: Parser.price.format(pending, balanceCurrency) })}
                            </Text.caption>
                        )}
                    </div>
                </div>

                <div className="wallet-account__details">
                    <div className="wallet-account__detail">
                        <Text.caption contrast={6} left={<IconArrowDownLeft />}>
                            {t("wallets.details.receiving")}
                        </Text.caption>
                        <Text.label
                            contrast={9}
                            right={status.receivingPositive ? <IconCircleCheck className="wallet-account__success-icon" /> : null}
                        >
                            {status.receiving}
                        </Text.label>
                    </div>
                    <div className="wallet-account__detail">
                        <Text.caption contrast={6} left={<IconArrowUpRight />}>
                            {t("wallets.details.payouts")}
                        </Text.caption>
                        <Text.label contrast={9}>{status.payouts}</Text.label>
                    </div>
                </div>

                <QuickWithdrawal
                    provider={provider}
                    wallet={wallet}
                    currency={balanceCurrency}
                    onStripeWithdrawal={handleAction}
                />

                <WalletFees
                    provider={provider}
                    wallet={wallet}
                    loading={loading}
                />

                <div className="wallet-account__actions">
                    {(!connected || (provider === WALLET_PROVIDERS.STRIPE && !walletHasWithdrawalAccount(wallet))) && (
                        <Button
                            color={connected ? "overlay" : "inverse"}
                            size="small"
                            startIcon={status.actionIcon}
                            loading={submitting}
                            onClick={handleAction}
                        >
                            {status.actionText}
                        </Button>
                    )}
                </div>
            </div>
        </Card>
    );
}

export default function WalletConnectionCards({
    asaas,
    stripe,
    loading,
    onProviderChange,
    onOpenAsaasOnboarding,
    onRefresh,
}) {
    return (
        <div className="wallet-connection-cards">
            <WalletAccountCard
                provider={WALLET_PROVIDERS.ASAAS}
                wallet={asaas}
                loading={loading}
                onProviderChange={onProviderChange}
                onOpenAsaasOnboarding={onOpenAsaasOnboarding}
                onRefresh={onRefresh}
            />
            <WalletAccountCard
                provider={WALLET_PROVIDERS.STRIPE}
                wallet={stripe}
                loading={loading}
                onProviderChange={onProviderChange}
                onOpenAsaasOnboarding={onOpenAsaasOnboarding}
                onRefresh={onRefresh}
            />
        </div>
    );
}
